import React, { useEffect, useState } from 'react';
import { MediaItem, MediaType } from '../../api/structs/media';
import { MediaOutputSpec } from '../../api/structs/media-spec';
import Modal from './Modal';
import RegenerateForm, { FrameCapabilities } from './RegenerateForm';
import {
  UNKNOWN_VIDEO_CAPABILITIES,
  supportsLastFrameOf,
} from '../../api/videoCapabilities';
import SceneFrameThumbs, { BoundFrame } from './SceneFrameThumbs';
import { hasNewFrame, useResolvedFrame } from './useResolvedFrame';
import { SceneGroup } from '../clips/sections/MediaEditorSection.helpers';
import { RegenerateWatchMap } from '../clips/sections/regenerateWatch';
import SceneMediaStatus from './SceneMediaStatus';
import SceneFrameHandshakeBanner from './SceneFrameHandshakeBanner';
import ReferenceFramesEditor from '../clips/sections/ReferenceFramesEditor';
import {
  ReferenceFrameState,
  bindingFromMediaItem,
  filterReferenceFrameCandidates,
  readReferenceFrames,
} from '../clips/sections/referenceFrames';
import {
  ReferenceInputState,
  emptyReferenceInputs,
  readReferenceInputs,
} from '../clips/sections/referenceInputs';

export type SceneRegenerateTab = 'video' | 'frames';

interface SceneRegenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * LIVE scene group — the mount site re-resolves it by scene id every render,
   * so an in-place frame regeneration (same media id, new file_url) surfaces
   * here while the modal is open. That prop drift is the whole "new frame"
   * detection mechanism; never snapshot the scene at the call site.
   */
  scene: SceneGroup;
  initialTab: SceneRegenerateTab;
  getOutputSpec: (type: MediaType) => MediaOutputSpec | undefined;
  /**
   * In-flight regenerations owned by MediaEditorSection, keyed by media id.
   * The modal cannot hold this itself — it used to unmount on submit, and even
   * now that the video pane stays open, a user who closes it must not lose the
   * "regenerating…" state on the scene behind it.
   */
  regenWatches?: RegenerateWatchMap;
  /** Called with the media id whose regeneration was just accepted (HTTP 200). */
  onRegenerateStarted?: (mediaId: string) => void;
  onRefresh: () => void;
}

const NO_FRAMES: ReferenceFrameState = { first: null, last: null, lastEnabled: false };

const tabButton = (active: boolean) =>
  `flex-1 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${
    active
      ? 'border-white/50 text-white bg-white/5'
      : 'border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/25'
  }`;

/**
 * ONE regeneration dialog for a storyboard scene, replacing the old wiring
 * where the big Regenerate button and the hover ↻ hit the reference IMAGE
 * while only a small side-row link reached the video.
 *
 * Split at the top into Video | Frames:
 *  - Video: regenerate the scene's video, frames in CONTROLLED mode so a fresh
 *    take reaches the payload. A frame REGENERATED while this dialog is open
 *    refreshes its own slot automatically: the slot still names that very media
 *    item, so following its bytes is not a rewire — it is the difference
 *    between generating from the take the operator just made and generating
 *    from the one they replaced. Binding a DIFFERENT image is still an explicit
 *    act ("Use new … frame", the swap strip, the library picker).
 *  - Frames: the frames the video is bound to, as pictures, then one full
 *    RegenerateForm per frame that resolves to a real media item, plus a
 *    view/swap/clear editor for any bound frame that does not.
 *
 * The Frames tab is offered whenever the VIDEO carries frame bindings, not (as
 * it was) only when the clip also holds an image item. That gate silently
 * excluded every VIDEO-ONLY reel — a final-assembly template emitting
 * `imagePrompts: []` puts no still on the clip at all, so `scene.image` is
 * undefined and the whole Frames half of this dialog was unreachable even
 * though the video plainly had an opening and a closing frame.
 *
 * NEVER "fix" a missing frame by creating an image media item on the clip
 * (`API.createImage(clipId, …)`). The clip's image bucket is the reel's slide
 * list: the editor timeline and the renderer both turn any image there into a
 * visible IMG slide. A still added to make this dialog happy would appear in
 * the finished video — which is precisely what video-only reels exist to avoid.
 * Frames belong to the pipeline run; reach them by media id, never by copying
 * them onto the clip.
 */
const SceneRegenerateModalBody: React.FC<SceneRegenerateModalProps> = ({
  isOpen,
  onClose,
  scene,
  initialTab,
  getOutputSpec,
  regenWatches = {},
  onRegenerateStarted,
  onRefresh,
}) => {
  const video = scene.video;
  const image = scene.image;

  const [selectedTab, setSelectedTab] = useState<SceneRegenerateTab>(initialTab);
  // The bindings the video is opened WITH. Read once per OPEN (the exported
  // wrapper remounts this body on every open, which is what makes "once" mean
  // "fresh"), then held: `videoFrames` below moves as the user rebinds in the
  // Video tab, and the Frames tab must keep addressing the frames this video
  // was actually made from — re-deriving it would also re-fetch on every
  // rebind.
  const [initialFrames] = useState<ReferenceFrameState>(() =>
    video ? readReferenceFrames(video) : NO_FRAMES
  );
  // Controlled bindings for the video form; the banner buttons write into this.
  const [videoFrames, setVideoFrames] = useState<ReferenceFrameState>(initialFrames);
  // Reference INPUTS, owned here for the same reason the frames are: the
  // fallback editor in the Frames pane and the form in the Video pane must
  // edit one state, or a reference added in one pane vanishes on submit.
  const [videoReferences, setVideoReferences] = useState<ReferenceInputState>(() =>
    video ? readReferenceInputs(video) : emptyReferenceInputs()
  );
  const [frameCaps, setFrameCaps] = useState<FrameCapabilities>({
    known: false,
    caps: UNKNOWN_VIDEO_CAPABILITIES,
  });

  // The VIDEO'S BINDING decides which still each slot is about; the clip's own
  // image is only the fallback for a slot nothing binds (an image-only scene).
  const opening = useResolvedFrame(initialFrames.first, scene.allItems, image);
  const closing = useResolvedFrame(initialFrames.last, scene.allItems);

  // A closing frame that IS the opening still would render the same form twice —
  // one form covers both bindings then.
  const liveOpening = opening.item;
  const liveClosing =
    closing.item && closing.item.id !== liveOpening?.id ? closing.item : undefined;

  // What each slot is ABOUT. For a slot the video binds, that is `bound` and
  // only `bound`: `item` may be the clip's still standing in as a regeneration
  // target for a binding that reached nothing (a legacy URL-only frame), and
  // that still is not the frame. For a slot the video does NOT bind, the
  // scene's own still IS the frame this video would open on — which is how a
  // still regenerated here can be offered to a video that names no frame yet.
  const openingSubject = initialFrames.first ? opening.bound : opening.item;
  const closingSubject = initialFrames.last ? closing.bound : closing.item;
  // Every claim — the refresh, the banner, the strip's picture and its
  // "regenerated" tag — is made off that subject, against the baseline
  // `useResolvedFrame` latched off the same one.
  const openingRegenerated = hasNewFrame(opening.baseline, openingSubject);
  const closingRegenerated = hasNewFrame(closing.baseline, closingSubject);

  /**
   * Follow a slot's OWN media item when its bytes move.
   *
   * A frame regeneration keeps the media id and moves `file_url` (each take
   * lives at its own URL), so the video's stored `reference_frames[].ref.url`
   * names the take that was replaced. Left alone, "regenerate the frame, then
   * regenerate the video" generated the video from the frame the operator had
   * just thrown away — the reported bug. Refreshing the URL of a slot that
   * still names that same media item is not a rewire, and the guard is exactly
   * that: a slot the operator has pointed somewhere else (`mediaId` no longer
   * matches) is never touched.
   */
  const refreshSlot = (
    slot: 'first' | 'last',
    live: MediaItem | undefined,
    baselineUrl: string | undefined,
    regenerated: boolean
  ) => {
    if (!live || !regenerated) return;
    const next = bindingFromMediaItem(live);
    if (!next) return;
    setVideoFrames((prev) => {
      const current = prev[slot];
      if (!current || current.url === next.url) return prev;
      // "Still this item": by id when the binding carries one, else by the URL
      // the slot opened with (legacy URL-only frames). Anything else is the
      // operator's own pick and stays put.
      const stillNames =
        (!!current.mediaId && current.mediaId === live.id) ||
        (!!baselineUrl && current.url === baselineUrl);
      if (!stillNames) return prev;
      return slot === 'first' ? { ...prev, first: next } : { ...prev, last: next };
    });
  };
  useEffect(() => {
    refreshSlot('first', opening.bound, opening.baseline?.fileUrl, openingRegenerated);
    refreshSlot('last', closing.bound, closing.baseline?.fileUrl, closingRegenerated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening.bound?.id, opening.bound?.file_url, closing.bound?.id, closing.bound?.file_url]);

  if (!video && !image) return null;

  // A bound slot that reached no media item: view/swap/clear only, never a
  // regenerate button. The commonest case by far is the CLOSING frame of a clip
  // made before assembly.go stamped `last_reference_media_id` — its URL is a
  // provider CDN link with nothing of ours behind it.
  //
  // "Reached no media item" is `bound`, not `item`: a slot whose binding failed
  // may still hand the operator the scene's own still to regenerate (see
  // useResolvedFrame), and that rescue must not make the slot look resolved —
  // the swap/clear editor and the honest notice are still what it needs.
  const unresolvedSlots: string[] = [];
  if (initialFrames.first && !opening.bound && !opening.loading) unresolvedSlots.push('opening');
  if (initialFrames.last && !closing.bound && !closing.loading) unresolvedSlots.push('closing');

  // Frames are reachable when the scene owns a still OR the video names any
  // frame at all — the second half is what a video-only reel has.
  const hasFramesPane = !!image || !!initialFrames.first || !!initialFrames.last;
  const hasTabs = !!video && hasFramesPane;
  const tab: SceneRegenerateTab = !video ? 'frames' : !hasFramesPane ? 'video' : selectedTab;

  const newOpening = openingRegenerated;
  const newClosing = !!liveClosing && closingRegenerated;

  // The picture for a slot: the bound item's, or — for a binding that reached
  // nothing — the binding URL itself. Never the rescue still, which is a
  // regeneration target and not what the video opens on.
  const openingThumb = openingSubject;
  const closingThumb = closingSubject;

  // The frames pane leads with the frames themselves. Bindings first, so a slot
  // pointing away from the clip's own still shows what the video really opens
  // on; a slot no video binds (image-only scene) falls back to the still.
  const boundFrames: BoundFrame[] = [];
  if (initialFrames.first || openingThumb) {
    boundFrames.push({
      slot: 'first',
      // The strip names the video's SLOTS (first/last), not the model's
      // vocabulary — the editor below already speaks that.
      label: 'Opening frame',
      binding: initialFrames.first,
      item: openingThumb,
      regenerated: openingRegenerated,
    });
  }
  if (initialFrames.last || closingThumb) {
    boundFrames.push({
      slot: 'last',
      label: 'Closing frame',
      binding: initialFrames.last,
      item: closingThumb,
      regenerated: closingRegenerated,
    });
  }

  // The binding each handshake button would write. Off the SUBJECT, so a slot
  // the video does not bind yet can still be filled with the still the operator
  // just regenerated — that is the whole point of the button, and requiring
  // `bound` meant a video with no frame binding could never be offered one.
  const openingBinding = openingSubject ? bindingFromMediaItem(openingSubject) : null;
  const closingBinding =
    closingSubject && closingSubject.id !== openingSubject?.id
      ? bindingFromMediaItem(closingSubject)
      : null;
  const openingBound = !!openingBinding && videoFrames.first?.url === openingBinding.url;
  const closingBound =
    !!closingBinding && videoFrames.lastEnabled && videoFrames.last?.url === closingBinding.url;

  const showUseOpening = newOpening && !!openingBinding;
  // Closing rebind rides the reference_frames payload, which the model must
  // support — without `last` in its frame positions the button would bind a
  // frame the submit path then drops.
  const showUseClosing = newClosing && !!closingBinding && supportsLastFrameOf(frameCaps.caps);

  const banner = (
    <SceneFrameHandshakeBanner
      openingBinding={showUseOpening ? openingBinding : null}
      closingBinding={showUseClosing ? closingBinding : null}
      openingBound={openingBound}
      closingBound={closingBound}
      onUseOpening={(binding) => setVideoFrames((prev) => ({ ...prev, first: binding }))}
      onUseClosing={(binding) =>
        setVideoFrames((prev) => ({ ...prev, last: binding, lastEnabled: true }))
      }
    />
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Regenerate Scene">
      <div className="space-y-4">
        {hasTabs && (
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="scene-regen-tab-video"
              className={tabButton(tab === 'video')}
              aria-pressed={tab === 'video'}
              onClick={() => setSelectedTab('video')}
            >
              Video
            </button>
            <button
              type="button"
              data-testid="scene-regen-tab-frames"
              className={tabButton(tab === 'frames')}
              aria-pressed={tab === 'frames'}
              onClick={() => setSelectedTab('frames')}
            >
              Frames
            </button>
          </div>
        )}

        {/* Both panes stay MOUNTED across tab switches (`hidden` attribute, not
            unmount) so in-progress prompt/provider edits survive a hop to the
            other tab — the whole handshake is "regenerate a frame over there,
            come back here and bind it". */}
        {video && (
          <div data-testid="scene-regen-video-pane" hidden={tab !== 'video'}>
            <SceneMediaStatus item={video} role="video" watches={regenWatches} />
            <RegenerateForm
              item={video}
              outputSpec={getOutputSpec('ai_video')}
              sceneImages={filterReferenceFrameCandidates(scene.allItems, video.id)}
              frames={videoFrames}
              onFramesChange={setVideoFrames}
              references={videoReferences}
              onReferencesChange={setVideoReferences}
              onFrameCapsChange={setFrameCaps}
              banner={banner}
              // Deliberately does NOT close, matching the Frames pane.
              //
              // The old auto-close is what made a failed video regeneration
              // invisible: the dialog vanished on a green "Regeneration
              // started" toast, the canonical item is untouched by design for
              // the whole provider call, and so the storyboard behind it
              // re-rendered byte-identical. Staying open keeps the user on the
              // surface where the outcome lands — the status line above turns
              // into the provider's actual reason — and `onRegenerateStarted`
              // makes the wait survive them closing it anyway. Cancel/✕ still
              // close on demand.
              onSubmitted={() => {
                onRegenerateStarted?.(video.id);
                onRefresh();
              }}
              onCancel={onClose}
            />
          </div>
        )}

        {hasFramesPane && (
          <div data-testid="scene-regen-frames-pane" hidden={tab !== 'frames'} className="space-y-6">
            {/* A run-owned frame is fetched, so this pane can legitimately open
                empty for a moment. Say what is happening rather than flashing
                "nothing here". */}
            {(opening.loading || closing.loading) && !liveOpening && !liveClosing && (
              <p
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500"
                data-testid="frames-resolving"
              >
                resolving frames…
              </p>
            )}

            {/* What the video is made from, as pictures. Two prompt forms with
                no images could not say which still each one was about. */}
            <SceneFrameThumbs frames={boundFrames} />

            {liveOpening && (
              <div className="space-y-2">
                {/* The strip above already names the frames; this names the
                    ACTION, so two identical-looking forms are told apart. */}
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  Regenerate opening frame
                </p>
                <SceneMediaStatus item={liveOpening} role="frame" watches={regenWatches} />
                {/* Submitting a frame keeps the modal OPEN: the point is to wait
                    for the new still and then bind it in the Video tab. */}
                <RegenerateForm
                  item={liveOpening}
                  outputSpec={getOutputSpec('image')}
                  submitLabel="Regenerate frame"
                  onSubmitted={() => {
                    onRegenerateStarted?.(liveOpening.id);
                    onRefresh();
                  }}
                />
              </div>
            )}

            {liveClosing && (
              <div className="space-y-2 border-t border-white/10 pt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  Regenerate closing frame
                </p>
                <SceneMediaStatus item={liveClosing} role="frame" watches={regenWatches} />
                <RegenerateForm
                  item={liveClosing}
                  outputSpec={getOutputSpec('image')}
                  submitLabel="Regenerate frame"
                  onSubmitted={() => {
                    onRegenerateStarted?.(liveClosing.id);
                    onRefresh();
                  }}
                />
              </div>
            )}

            {/* Bound frames we could not resolve to media of ours. Everything
                below genuinely works on a bare URL — look at it, swap it for a
                library image, clear it, break the chain — and all of it rides
                the SAME controlled bindings the Video tab submits, so a swap
                made here is a swap made there. `onRegenerateImage` is omitted
                on purpose: there is no media item to regenerate, and a button
                that pretends otherwise is worse than no button. */}
            {video && unresolvedSlots.length > 0 && (
              <div
                data-testid="scene-regen-frames-fallback"
                className={liveOpening || liveClosing ? 'border-t border-white/10 pt-4' : undefined}
              >
                <ReferenceFramesEditor
                  state={videoFrames}
                  onChange={setVideoFrames}
                  candidates={filterReferenceFrameCandidates(scene.allItems, video.id)}
                  caps={frameCaps.caps}
                  capabilityKnown={frameCaps.known}
                  references={videoReferences}
                  onReferencesChange={setVideoReferences}
                />
                <p
                  className="mt-2 font-mono text-[10px] leading-relaxed text-amber-200/70"
                  data-testid="frames-not-regenerable"
                >
                  No media item behind the {unresolvedSlots.join(' and ')}{' '}
                  {unresolvedSlots.length > 1 ? 'frames' : 'frame'} on this clip —{' '}
                  {unresolvedSlots.length > 1 ? 'they' : 'it'} can be swapped or cleared here, but
                  not regenerated.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

/**
 * Every OPEN gets a fresh body.
 *
 * The bindings, the "which item did this slot name" latch and the new-frame
 * baselines are all first-sight state, and first sight has to mean the moment
 * the dialog opened — not the moment its host happened to mount it. A host that
 * keeps this mounted across a close (the clips section does not, but nothing
 * stops one) otherwise reopened the dialog still addressing the bindings the
 * video had before its last regeneration. Remounting on the false→true edge is
 * the whole mechanism, and it costs a single state bump.
 *
 * The edge is detected DURING render, not in an effect. An effect-driven bump
 * lands one commit LATE: the dialog mounts whole under the old key, runs every
 * lookup inside it (the model capabilities, the forms, the frame reads a fresh
 * body still owes), and is then thrown away for a second mount that runs them
 * all again — two of everything for one open. Adjusting state while rendering
 * is React's own escape hatch for exactly this: the component re-runs before
 * its children render, so the discarded mount never happens.
 */
const SceneRegenerateModal: React.FC<SceneRegenerateModalProps> = (props) => {
  const [openCount, setOpenCount] = useState(0);
  const [wasOpen, setWasOpen] = useState(props.isOpen);
  if (props.isOpen !== wasOpen) {
    setWasOpen(props.isOpen);
    if (props.isOpen) setOpenCount((count) => count + 1);
  }

  return <SceneRegenerateModalBody key={openCount} {...props} />;
};

export default SceneRegenerateModal;
